/**
 * 대시보드.
 *
 * 운영자가 아침에 콘솔을 열고 **10초 안에** 답을 얻어야 하는 질문만 담는다:
 * 지금 문제가 있나, 어디가 문제인가, 누가 최근에 뭘 바꿨나.
 *
 * 숫자를 예쁘게 늘어놓는 것이 목적이 아니라서, 클릭하면 그 숫자를 만든 목록으로
 * 바로 간다. 대시보드에서 끝나는 정보는 대시보드에 둘 이유가 없다.
 *
 * **무엇을 볼지는 보는 사람이 고른다.** 현장 담당자는 꺼진 화면을, 광고 운영자는 재생
 * 실적을 본다 — 모두에게 같은 판을 내밀면 누구에게나 절반은 잡음이다. [편집] 을 누르면
 * 올릴 수 있는 카드 목록이 나오고, 고른 것만 그린다. 카드의 정의는 `dashboardCards.ts`
 * 한 표에 있고 이 화면은 그 표를 읽어 그리기만 한다.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronRight,
  LayoutGrid,
  Monitor,
  MonitorX,
  X,
} from 'lucide-react';
import type { Asset, Display, FleetSummary, Me, SignageOneClient } from '../api/client.js';
import { describeError, useAsync, usePolling } from '../hooks.js';
import { formatSeconds, relativeTime, RelTime, StatusDot } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { browserTimeZone, dayStartIso, localDate } from '../components/localDate.js';
import { ACTION_LABEL, formatActor, needsResourceLabel, RESOURCE_LABEL } from './Activities.js';
import { validityBadge } from '../validity.js';
import { plannedGroupIds } from '../groups.js';
import {
  availableCards,
  cardById,
  dashboardCardsKey,
  LEGACY_DASHBOARD_CARDS_KEY,
  defaultLayout,
  GRID_COLUMNS,
  normalizeLayout,
  readLayout,
  resizeCard,
  sameLayout,
  toggleCard,
  type CardPlacement,
  type DashboardCard,
} from './dashboardCards.js';
/*
 * 끌기 부품은 모니터링 벽과 **같은 것**을 쓴다 — 유령·FLIP·자동 스크롤·끌기 판정.
 * 두 벌로 두면 한쪽만 고쳐져, 같은 콘솔 안에서 카드를 끄는 느낌이 화면마다 달라진다.
 */
import {
  createGhost,
  dragCorner,
  DRAG_THRESHOLD,
  insertionIndex,
  edgeScrollSpeed,
  moveTo,
  scrollParentOf,
  suppressNextClick,
  TOUCH_HOLD_MS,
  TOUCH_SLOP,
  useFlip,
  type Ghost,
} from '../components/wallDrag.js';
import { canOpen, navCapabilities } from '../nav.js';
import { displayLive } from '../components/displayLive.js';

const TYPE_LABEL: Record<Asset['type'], string> = {
  image: '이미지',
  video: '영상',
  web: '웹',
  template: '템플릿',
  html: 'HTML',
};

/* 유형 색은 상태 색(초록·노랑·빨강)과 겹치지 않게 고른다 — 도넛의 초록 조각이
 * "정상"으로 읽히면 안 된다. */
const TYPE_COLOR: Record<Asset['type'], string> = {
  image: '#3b82f6',
  video: '#8b5cf6',
  web: '#0ea5e9',
  template: '#f59e0b',
  html: '#14b8a6',
};

/**
 * 화면 현황 카드의 줄 수.
 *
 * 위치별 숫자는 **서버가 센다**(`/displays/summary?locations=1`). 예전에는 콘솔이 화면 목록을 받아 셌는데,
 * 서버 기본값(50)으로 받던 동안은 51대부터 숫자가 조용히 틀렸고, 끝까지 받게 고친 뒤로는 화면 전부를 30초마다
 * 받았다. 화면 현황 카드는 여덟 줄만 그리므로 그만큼만 받는다.
 */
const DISPLAY_CARD_ROWS = 8;

export function DashboardScreen({ client, me }: { client: SignageOneClient; me: Me }): ReactNode {
  const capabilities = me.capabilities;
  /**
   * 지금 보고 있는 판.
   *
   * **첫 그림은 이 브라우저에 남겨 둔 사본으로 그린다.** 저장소는 서버지만, 응답을
   * 기다리는 동안 기본 판을 그렸다가 바꾸면 새로고침할 때마다 카드들이 한 번 튀었다
   * 제자리를 찾는다.
   */
  /*
   * 브라우저 사본의 열쇠는 **이 계정의 것**이다. 공용 PC 에서 계정을 바꾸면 앞사람 판이
   * 그대로 보이던 일을 막는다(서버는 처음부터 계정별로 갖고 있었다).
   */
  const storageKey = dashboardCardsKey(me.account?.id ?? me.id);
  const [layout, setLayout] = useState<CardPlacement[]>(() =>
    readLayout(readStoredCards(storageKey), capabilities),
  );
  const [editing, setEditing] = useState(false);
  /** 계정에 저장하지 못했을 때의 한 줄. 이 브라우저에는 남으므로 화면은 그대로 바뀐다. */
  const [saveError, setSaveError] = useState<string | null>(null);

  /*
   * 서버에 저장된 내 판. 한 번만 받아 온다 — 판은 이 사람이 바꿀 때만 바뀌므로 다시
   * 물을 이유가 없고, 폴링으로 덮으면 편집 중인 판을 서버 값이 밀어낸다.
   */
  const saved = useAsync(() => client.dashboardLayout(), [client]);
  const adopted = useRef(false);
  useEffect(() => {
    if (adopted.current || saved.loading) return;
    adopted.current = true;
    /*
     * `cards: null` 은 "정한 적 없음" 이다 — 그때는 이 브라우저에 남은 **내** 사본
     * (또는 기본 판)을 그대로 둔다. 빈 배열은 "전부 내렸다" 는 선택이므로 그대로 따른다.
     */
    if (!saved.data?.cards) return;
    const next = normalizeLayout(saved.data.cards, capabilities);
    setLayout((current) => (sameLayout(current, next) ? current : next));
    rememberCards(storageKey, next);
  }, [saved.loading, saved.data, capabilities, storageKey]);

  /** 바뀐 판을 화면·브라우저·계정 셋에 남긴다. 계정 저장만 실패할 수 있다. */
  const applyLayout = (next: CardPlacement[]): void => {
    setLayout(next);
    rememberCards(storageKey, next);
    setSaveError(null);
    client.saveDashboardLayout(next).catch((cause: unknown) => {
      setSaveError(`계정에 저장하지 못했습니다(이 브라우저에만 저장됨): ${describeError(cause)}`);
    });
  };

  /* ── 카드 옮기기 ─────────────────────────────────────────────── */
  const gridRef = useRef<HTMLDivElement | null>(null);
  /** 끌고 있는 카드. 유령이 따라다니는 동안 원래 자리는 자리만 지킨다. */
  const [dragId, setDragId] = useState<string | null>(null);
  /** 끄는 동안 미리 보여 주는 순서. 놓으면 이것이 판이 된다. */
  const [preview, setPreview] = useState<CardPlacement[] | null>(null);
  const shown = preview ?? layout;
  /** 순서나 칸 수가 바뀌면 카드들이 새 자리로 미끄러져 간다 — 모니터링 벽과 같은 FLIP. */
  useFlip(gridRef, shown.map((card) => `${card.id}:${card.span}:${card.height ?? ''}`).join('|'));

  /** 끌기 중에도 늘 최신 판을 본다 — 핸들러는 시작할 때의 값을 붙들고 있다. */
  const latest = useRef(layout);
  latest.current = layout;

  /** 한 카드를 그 자리로 옮긴다. 끌기의 키보드 대체 수단(Alt + ←/→)이 쓴다. */
  const moveCard = (id: string, to: number): void => {
    const current = latest.current;
    const card = current.find((entry) => entry.id === id);
    if (!card) return;
    const next = moveTo(current, card, to);
    if (sameLayout(next, current)) return;
    applyLayout(next);
  };

  /** 지금 모서리를 끌고 있는 카드. 그동안은 칸 수 표시를 남긴다. */
  const [resizeId, setResizeId] = useState<string | null>(null);

  /**
   * 카드 폭과 높이를 **모서리를 끌어** 정한다.
   *
   * **끌어 간 폭을 칸 수로 되돌린다.** 격자가 칸으로 서 있으므로 픽셀을 그대로 담으면
   * 창 폭이 달라지는 순간 판이 무너진다 — 지금 격자의 칸 너비로 나눠 가장 가까운 칸 수를
   * 고른다. 창이 좁아 두 칸만 서는 화면에서는 두 칸까지만 닿는다(그 이상은 보이지도 않는다).
   *
   * **높이는 픽셀로 담는다**(2026-10-22 사용자 지시 "각 카드별로 높이 조정이 안된다") — 창 폭과 상관이 없다. 걸음(20px)에
   * 맞춰 이웃 카드와 끝을 맞추기 쉽게 한다. 옆으로만 끌려던 손이 조금 흔들려 높이가 고정되지 않게, 세로로
   * `HEIGHT_DRAG_FROM` 넘게 움직였을 때부터 높이를 바꾼다. 내용 높이로 되돌리는 것은 [높이 자동].
   */
  const startCardResize = (id: string, event: ReactPointerEvent<HTMLElement>): void => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const wrapper = (event.target as HTMLElement).closest<HTMLElement>('[data-flip]');
    const grid = gridRef.current;
    if (!wrapper || !grid) return;

    const style = getComputedStyle(grid);
    const columns = style.gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    const gap = Number.parseFloat(style.columnGap) || 0;
    const unit = (grid.getBoundingClientRect().width - gap * (columns - 1)) / columns;
    const startWidth = wrapper.getBoundingClientRect().width;
    const startHeight = wrapper.getBoundingClientRect().height;
    const before = latest.current;
    let next = before;
    let heightTouched = false;

    setResizeId(id);
    dragCorner(
      event,
      (dx, dy) => {
        const span = Math.max(1, Math.min(columns, Math.round((startWidth + dx + gap) / (unit + gap))));
        if (Math.abs(dy) >= HEIGHT_DRAG_FROM) heightTouched = true;
        const moved = resizeCard(before, id, span, heightTouched ? startHeight + dy : undefined);
        if (sameLayout(moved, next)) return;
        next = moved;
        setPreview(moved);
      },
      (commit) => {
        setResizeId(null);
        setPreview(null);
        if (commit && !sameLayout(next, before)) applyLayout(next);
      },
    );
  };

  /**
   * 카드를 잡아 끈다.
   *
   * 모니터링 벽과 같은 부품(유령·FLIP·자동 스크롤)을 쓴다. 브라우저 기본 끌기는 손가락을
   * 받지 않고, 놓을 자리를 알리려면 이동할 때마다 판 전체를 다시 그려야 해서 끊겨 보인다.
   */
  const startCardDrag = (id: string, event: ReactPointerEvent<HTMLElement>): void => {
    if (!editing || event.button !== 0) return;
    const source = (event.target as HTMLElement).closest<HTMLElement>('[data-flip]');
    if (!source) return;

    const touch = event.pointerType === 'touch';
    const pointerId = event.pointerId;
    const start = { x: event.clientX, y: event.clientY };
    const pointer = { ...start };
    let active = false;
    let ghost: Ghost | null = null;
    let order: CardPlacement[] | null = null;
    let scroller: HTMLElement | null = null;
    let frame = 0;

    /*
     * 놓을 자리는 **포인터가 가리키는 틈**이다 — 다른 카드 위에 정확히 올라갈 필요가 없다.
     * 모니터링 벽과 같은 규칙(`insertionIndex`)을 쓴다.
     */
    const track = (): void => {
      const slots = [...(gridRef.current?.querySelectorAll<HTMLElement>(':scope > [data-flip]') ?? [])]
        .filter((node) => node.dataset.flip !== id)
        .map((node) => {
          const box = node.getBoundingClientRect();
          return { id: node.dataset.flip ?? '', left: box.left, right: box.right, top: box.top, bottom: box.bottom };
        });

      const current = order ?? latest.current;
      const moving = current.find((card) => card.id === id);
      if (!moving) return;
      const rest = current.filter((card) => card.id !== id);
      const at = insertionIndex(slots, pointer);
      const next = [...rest.slice(0, at), moving, ...rest.slice(at)];
      if (sameLayout(next, current)) return;
      order = next;
      setPreview(next);
    };

    const activate = (): void => {
      if (active) return;
      active = true;
      window.clearTimeout(hold);
      ghost = createGhost(source, start);
      ghost.move(pointer.x, pointer.y);
      scroller = scrollParentOf(gridRef.current);
      setDragId(id);
      if (touch) navigator.vibrate?.(10);

      const tick = (): void => {
        const bounds = scroller ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
        const speed = edgeScrollSpeed(pointer.y, bounds.top, bounds.bottom);
        if (speed !== 0) {
          if (scroller) scroller.scrollTop += speed;
          else window.scrollBy(0, speed);
          track();
        }
        frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
    };

    const end = (commit: boolean): void => {
      window.clearTimeout(hold);
      window.cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('touchmove', onTouchMove);
      if (!active) return;
      suppressNextClick();

      const next = order;
      // 미리 보기를 걷는 것과 새 판을 세우는 것을 한 렌더에 한다 — 그래야 카드가 튀지 않는다.
      setPreview(null);
      setDragId(null);
      if (commit && next && !sameLayout(next, latest.current)) applyLayout(next);

      const flying = ghost;
      window.requestAnimationFrame(() => {
        const spot = document.querySelector<HTMLElement>(`[data-flip="${CSS.escape(id)}"]`);
        flying?.settle(spot?.getBoundingClientRect() ?? null);
      });
    };

    const onMove = (moved: PointerEvent): void => {
      if (moved.pointerId !== pointerId) return;
      pointer.x = moved.clientX;
      pointer.y = moved.clientY;
      if (!active) {
        const far = Math.hypot(pointer.x - start.x, pointer.y - start.y);
        // 손가락은 누르고 있어야 끌기다 — 바로 끌리면 페이지를 스크롤할 수 없다.
        if (touch) {
          if (far > TOUCH_SLOP) end(false);
        } else if (far >= DRAG_THRESHOLD) {
          activate();
        }
        return;
      }
      ghost?.move(pointer.x, pointer.y);
      track();
    };
    const onUp = (up: PointerEvent): void => {
      if (up.pointerId === pointerId) end(true);
    };
    const onCancel = (cancelled: PointerEvent): void => {
      if (cancelled.pointerId === pointerId) end(false);
    };
    const onKey = (key: KeyboardEvent): void => {
      if (!active || key.key !== 'Escape') return;
      key.preventDefault();
      key.stopPropagation();
      end(false);
    };
    const onTouchMove = (move: TouchEvent): void => {
      if (active) move.preventDefault();
    };

    const hold = touch ? window.setTimeout(activate, TOUCH_HOLD_MS) : 0;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
  };

  const on = (id: string): boolean => layout.some((card) => card.id === id);
  /*
   * **꺼 둔 카드의 데이터는 받지 않는다.** 대시보드는 30초마다 스스로 다시 부르는
   * 화면이라, 아무도 보지 않는 카드의 조회가 하루 종일 서버를 두드리게 된다.
   */
  /*
   * 재생 실적 카드도 함대 요약을 쓴다 — "틀고 있어야 할 화면이 있는가" 가 경보의 전제다.
   * 위치별 현황·화면 현황·시작하기 안내도 요약의 수로 그린다(아래).
   */
  const needLocations = on('regions');
  const needDisplays = on('displays');
  const needFleet = on('summary') || on('playToday') || needLocations || needDisplays;
  const needAssets = on('summary') || on('contentTypes');
  const needPlaylists = on('summary');

  /*
   * **화면 전부를 받지 않는다.** 위치별 현황은 서버가 센 것을(`locations=1`), 화면 현황은 손볼 화면 여덟 대와
   * 첫 쪽 여덟 대만 받는다. 예전에는 두 카드를 위해 화면 전부(최대 5,000대, 200대씩 25번)를 30초마다 받았다 —
   * 대시보드는 하루 종일 열어 두는 첫 화면이라, 화면이 많은 현장에서는 열린 탭 하나가 그만큼 서버를 두드렸다.
   */
  const fleet = useAsync(
    () => (needFleet ? client.fleetSummary({ locations: needLocations }) : Promise.resolve(null)),
    [client, needFleet, needLocations],
  );
  const displays = useAsync(
    () =>
      needDisplays
        ? Promise.all([
            client.listDisplays({ status: 'attention', limit: String(DISPLAY_CARD_ROWS) }),
            client.listDisplays({ limit: String(DISPLAY_CARD_ROWS) }),
            // '미배정' 은 그룹 편성까지 보고 가린다 — 그룹은 손에 꼽을 만큼이라 함께 받는다.
            client.listGroups(),
          ]).then(([attention, first, groups]) => {
            const planned = plannedGroupIds(groups.items);
            return { rows: displayCardRows(attention.items, first.items, planned), planned };
          })
        : Promise.resolve(null),
    [client, needDisplays],
  );
  const assets = useAsync(
    () => (needAssets ? client.allAssets({ archived: 'false' }) : Promise.resolve(null)),
    [client, needAssets],
  );
  const playlists = useAsync(
    () => (needPlaylists ? client.allPlaylists() : Promise.resolve(null)),
    [client, needPlaylists],
  );
  const schedules = useAsync(
    () => (needPlaylists ? client.listSchedules() : Promise.resolve(null)),
    [client, needPlaylists],
  );
  const audit = useAsync(
    () => (on('activity') ? client.auditLogs({ limit: '8' }) : Promise.resolve(null)),
    [client, on('activity')],
  );
  const needExpiring = on('expiring');
  const expiring = useAsync(
    () =>
      needExpiring
        ? client.listAssets({ archived: 'false', validity: 'expiring', within_days: '7', limit: '8' })
        : Promise.resolve(null),
    [client, needExpiring],
  );
  const needModules = on('modules');
  const modules = useAsync(
    () => (needModules ? client.exposedModules() : Promise.resolve(null)),
    [client, needModules],
  );
  const needPlay = on('playToday');
  const play = useAsync(() => (needPlay ? loadPlayToday(client) : Promise.resolve(null)), [
    client,
    needPlay,
  ]);

  usePolling(fleet.reload, 30_000, needFleet);
  usePolling(displays.reload, 30_000, needDisplays);
  /*
   * 재생 실적은 1분마다. 증빙은 화면들이 폴링 주기(기본 30초)마다 밀어 올리므로 그보다
   * 자주 물어도 같은 답이 오고, 이 카드 한 장이 집계 질의 셋을 부른다.
   */
  usePolling(play.reload, 60_000, needPlay);

  const items = displays.data?.rows ?? [];
  const plannedGroups = displays.data?.planned ?? NO_GROUPS;
  const locations = useMemo(() => locationRows(fleet.data?.locations ?? []), [fleet.data]);
  const typeCounts = useMemo(() => countTypes(assets.data?.items ?? []), [assets.data]);

  /*
   * **서버가 센 집합을 그대로 쓴다.** `offline + erroring` 으로 더했더니 오류를 보고한
   * 뒤 꺼진 화면이 두 번 세어져, 숫자가 목록보다 컸다. 목록(`?status=attention`)도 같은
   * 조건을 쓴다 — 세는 곳이 하나여야 눌러서 열린 목록의 줄 수와 숫자가 맞는다.
   */
  const attention = fleet.data?.attention ?? 0;

  /*
   * 시작하기 안내는 카드가 아니다 — 고르는 것이 아니라 **처음 켠 사람에게만** 잠깐
   * 나왔다 스스로 사라지는 것이다. 다만 판정에 쓰는 세 목록이 모두 있어야 말할 수 있다.
   * 카드를 꺼 둔 사람은 그 목록을 받지 않는데, 그 사람은 이미 이 안내를 지난 사람이다.
   */
  const steps =
    fleet.data && assets.data && playlists.data
      ? setupSteps({
          displayCount: fleet.data.total,
          assignedCount: fleet.data.total - fleet.data.unassigned,
          assetCount: assets.data.items.length,
          playlistCount: playlists.data.items.length,
          canInstall: capabilities.includes('system'),
        })
      : [];

  /**
   * 카드 하나의 알맹이.
   *
   * **판을 그리는 곳과 카드를 그리는 곳을 갈라 둔다.** 위에서는 어떤 카드가 어느 자리에
   * 몇 칸으로 서는지만 다루고, 여기서는 그 카드가 무엇을 보여주는지만 다룬다. 섞어 두면
   * 카드를 하나 더할 때마다 격자 코드를 함께 건드리게 된다.
   *
   * `null` 을 돌려주면 그 카드는 지금 보여줄 것이 없다는 뜻이다(위치가 하나뿐인 경우).
   * 판에서 내려간 것이 아니라 이번에만 비는 것이라, 편집 중에는 자리를 남겨 둔다.
   */
  const cardBody = (id: string): ReactNode => {
    switch (id) {
      case 'summary':
        return (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {/*
              **가장 큰 숫자는 제 이름표와 같은 것을 말해야 한다.**

              라벨이 "총 화면" 인데 값은 온라인 수였다 — 화면이 100대인 곳에서 "총 화면 3"
              을 보면 무슨 일이 났나 싶다. 값이 온라인 수이므로 이름표를 값에 맞춘다.
              아래 줄도 "0대 온라인" 으로 같은 수를 되풀이하고 있었다. 분모가 무엇인지
              말해 주는 편이 한 자리를 값어치 있게 쓴다.
            */}
            {/*
              **누른 사람은 그 숫자를 만든 화면들을 보러 간다.** 거르지 않은 목록으로 보내면
              "주의 필요 2" 를 눌러 놓고 40대짜리 목록에서 그 둘을 눈으로 찾아야 한다.
              그래서 숫자와 같은 뜻의 필터를 주소에 실어 보낸다(목록이 `?status` 를 읽는다).
            */}
            <StatCard
              to="/displays?status=online"
              label="온라인 화면"
              value={fleet.data ? `${fleet.data.online}` : '—'}
              suffix={fleet.data ? `/ ${fleet.data.total}` : ''}
              foot={
                fleet.data
                  ? // 숨긴 화면은 위 집계에서 빠져 있다 — 분모가 왜 그 수인지 여기서 밝힌다.
                    `등록된 화면 ${fleet.data.total}대${fleet.data.hidden > 0 ? ` · 숨김 ${fleet.data.hidden}` : ''}`
                  : '불러오는 중'
              }
              footTone={fleet.data && fleet.data.offline > 0 ? 'warn' : 'muted'}
            />
            <StatCard
              to="/content"
              label="활성 콘텐츠"
              value={assets.data ? String(assets.data.items.length) : '—'}
              foot={`템플릿 ${typeCounts.template ?? 0} · 이미지 ${typeCounts.image ?? 0}`}
              footTone="muted"
            />
            <StatCard
              to="/playlists"
              label="플레이리스트 · 스케줄"
              value={playlists.data ? String(playlists.data.items.length) : '—'}
              suffix={schedules.data ? `· ${schedules.data.items.length}` : ''}
              foot="편성 중"
              footTone="muted"
            />
            <StatCard
              to="/displays?status=attention"
              label="주의 필요"
              value={fleet.data ? String(attention) : '—'}
              foot={fleet.data ? attentionFoot(fleet.data) : '불러오는 중'}
              footTone={attention > 0 ? 'danger' : 'ok'}
            />
          </div>
        );
      case 'playToday':
        return (
          <PlayTodayCard
            data={play.data}
            error={play.error}
            loading={play.loading}
            playing={fleet.data ? playingDisplays(fleet.data) : null}
          />
        );
      /*
       * id 는 'regions' 로 남는다 — 계정마다 서버에 저장된 판이 이 id 로 카드를 가리킨다
       * (`/api/v1/me/dashboard`). 이름을 'locations' 로 고치면 이 카드를 올려 둔 사람의
       * 판에서 모르는 id 가 되어 카드가 조용히 사라진다. 코드 이름값보다 그 편이 비싸다.
       */
      case 'regions':
        /* 위치가 하나뿐이면 그리지 않는다 — 하나를 하나로 나눈 표는 자리만 차지한다. */
        return locations.length > 1 ? (
          <Card
            title="위치별 화면 현황"
            badge={`${locations.length}개 위치`}
            action={{ label: '전체 보기', to: '/displays' }}
          >
            {fleet.loading && !fleet.data ? (
              <p className="p-6 text-[13.5px] text-slate-400">불러오는 중…</p>
            ) : locations.length === 0 ? (
              <p className="p-6 text-[13.5px] text-slate-400">등록된 화면이 없습니다.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 xl:grid-cols-4">
                {locations.map((place) => (
                  <div key={place.name} className="rounded-lg border border-slate-200 p-4">
                    <div className="flex items-baseline justify-between">
                      <p className="truncate text-[13.5px] font-semibold text-slate-700">{place.name}</p>
                      <p className="shrink-0 font-mono text-[11.5px] text-slate-400">{place.total}대</p>
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-[12.5px]">
                      <span className="flex items-center gap-1.5 text-slate-600">
                        <StatusDot online />
                        {place.online}
                      </span>
                      {place.offline > 0 && (
                        <span className="flex items-center gap-1.5 text-slate-600">
                          <StatusDot online={false} />
                          {place.offline}
                        </span>
                      )}
                    </div>
                    {/* 막대는 비율을 그대로 보여준다 — 숫자만으로는 규모 차이가 안 잡힌다. */}
                    <div className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="bg-emerald-500"
                        style={{ width: `${(place.online / place.total) * 100}%` }}
                      />
                      <div
                        className="bg-slate-300"
                        style={{ width: `${(place.offline / place.total) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ) : null;
      case 'displays':
        return (
          <Card
            title="화면 현황"
            badge={fleet.data ? `${fleet.data.total}대` : undefined}
            action={{ label: '전체 보기', to: '/displays' }}
          >
            {items.length === 0 ? (
              <p className="p-6 text-[13.5px] text-slate-400">
                {displays.loading ? '불러오는 중…' : '등록된 화면이 없습니다.'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((display) => (
                    <li key={display.id}>
                      <Link
                        to={`/displays/${display.id}`}
                        state={{ from: '/' }}
                        className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-slate-50"
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                            display.online ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-600'
                          }`}
                        >
                          {display.online ? (
                            <Monitor className="h-4 w-4" strokeWidth={1.9} />
                          ) : (
                            <MonitorX className="h-4 w-4" strokeWidth={1.9} />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-[13.5px] font-medium text-slate-800">
                              {display.name}
                            </span>
                            <StatusDot online={display.online} />
                          </span>
                          <span className="mt-0.5 block truncate text-[11.5px] text-slate-400">
                            {display.location || '위치 미지정'} · <RelTime iso={display.status.last_heartbeat_at} />
                          </span>
                        </span>
                        {/* 끊긴 화면은 오프라인 하나만 — 마지막 보고에 남은 정비 모드·오류는 지금 상태가 아니다(`displayLive`). */}
                        {!display.online ? (
                          <span className="badge badge-neutral shrink-0">오프라인</span>
                        ) : display.status.last_error ? (
                          <span className="badge badge-danger shrink-0">오류</span>
                        ) : display.status.maintenance ? (
                          <span className="badge badge-warn shrink-0">정비 모드</span>
                        ) : isUnassigned(display, plannedGroups) ? (
                          <span className="badge badge-warn shrink-0">미배정</span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
              </ul>
            )}
          </Card>
        );
      case 'expiring':
        return (
          <Card title="곧 끝나는 콘텐츠" action={{ label: '모두 보기', to: '/content?validity=expiring' }}>
            {expiring.error ? (
              <p className="p-6 text-[13.5px] text-slate-400">{expiring.error}</p>
            ) : (expiring.data?.items.length ?? 0) === 0 ? (
              <p className="p-6 text-[13.5px] text-slate-400">7일 안에 게시가 끝나는 콘텐츠가 없습니다.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {expiring.data?.items.map((asset) => {
                  const badge = validityBadge(asset);
                  return (
                    <li key={asset.id}>
                      <Link to={`/content/${asset.id}`} className="flex items-center gap-2 px-4 py-2 hover:bg-slate-50">
                        <span className="min-w-0 flex-1 truncate text-[12.5px] text-slate-700">{asset.title}</span>
                        {badge ? <span className={`badge badge-${badge.tone} shrink-0`}>{badge.label}</span> : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        );
      case 'contentTypes':
        return (
          <Card title="콘텐츠 유형" action={{ label: '콘텐츠', to: '/content' }}>
            <TypeDonut counts={typeCounts} total={assets.data?.items.length ?? 0} />
          </Card>
        );
      case 'modules':
        return (
          <Card title="공개된 기능">
            {(modules.data?.modules.length ?? 0) === 0 ? (
              <p className="p-6 text-[13.5px] text-slate-400">
                {modules.error ? modules.error : '공개된 선택 기능이 없습니다.'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {modules.data?.modules.map((module) => (
                  <li key={module.id} className="px-4 py-2.5">
                    <p className="flex flex-wrap items-center gap-1.5 text-[12.5px] font-medium text-slate-700">
                      {module.name}
                      <span className={`badge ${module.enabled ? 'badge-ok' : 'badge-neutral'}`}>
                        {module.enabled ? '켜짐' : '꺼짐'}
                      </span>
                      {module.beta ? <span className="badge badge-warn">베타</span> : null}
                    </p>
                    <p className="text-[11.5px] text-slate-400">
                      {module.last_run
                        ? `${relativeTime(module.last_run.started_at)} · ${module.last_run.status === 'ok' ? '성공' : module.last_run.status === 'failed' ? '실패' : '실행 중'}`
                        : module.summary}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        );
      case 'activity':
        return (
          <Card
            title="최근 활동"
            // 활동 로그는 시스템 계정만 연다(`nav.ts`) — 열 수 없는 사람에게 막힌 주소로 가는 단추를 내지 않는다.
            action={canOpen('/activities', navCapabilities(me)) ? { label: '전체 보기', to: '/activities' } : undefined}
          >
            {audit.error ? (
              <p className="p-6 text-[13.5px] text-slate-400">
                활동 로그를 불러오지 못했습니다: {audit.error}
              </p>
            ) : (audit.data?.items.length ?? 0) === 0 ? (
              <p className="p-6 text-[13.5px] text-slate-400">기록된 활동이 없습니다.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {/*
                  동작 이름과 24자리 id 를 그대로 늘어놓으면 사람이 읽을 수 없다.
                  여기서는 "무슨 일이 있었나" 만 답하고, id 는 활동 로그에 맡긴다.
                */}
                {audit.data?.items.map((entry) => (
                  <li key={entry.id} className="flex items-center gap-3 px-4 py-2">
                    <CalendarClock className="h-3.5 w-3.5 shrink-0 text-slate-300" strokeWidth={2} />
                    <span className="min-w-0 flex-1 truncate text-[12.5px] text-slate-700">
                      {ACTION_LABEL[entry.action] ?? entry.action}
                      {/*
                        동작 이름이 이미 대상을 말하면 붙이지 않는다 —
                        "스케줄 수정" 뒤에 "스케줄" 을 또 붙이면 사람이 쓰지 않는 말이 된다.
                      */}
                      {needsResourceLabel(
                        ACTION_LABEL[entry.action] ?? entry.action,
                        RESOURCE_LABEL[entry.resource_type] ?? entry.resource_type,
                      ) ? (
                        <span className="ml-1.5 text-slate-400">
                          {RESOURCE_LABEL[entry.resource_type] ?? entry.resource_type}
                        </span>
                      ) : null}
                    </span>
                    <span className="max-w-[40%] shrink-0 truncate text-[11.5px] text-slate-400 sm:max-w-none">
                      {formatActor(entry.actor).name}
                    </span>
                    <span className="shrink-0 text-[11.5px] text-slate-400">
                      <RelTime iso={entry.created_at} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <SetupChecklist steps={steps} />

      {/*
        편집 손잡이는 오른쪽 끝에 혼자 둔다. 대시보드는 읽는 화면이라 위쪽에 버튼이
        늘어서면 그것부터 읽힌다 — 판을 바꾸는 일은 가끔 하는 일이다.
      */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {saveError ? <span className="text-[11.5px] text-amber-600">{saveError}</span> : null}
        <button
          type="button"
          className={`btn ${editing ? 'btn-primary' : ''}`}
          aria-pressed={editing}
          onClick={() => setEditing((value) => !value)}
        >
          <LayoutGrid className="h-3.5 w-3.5" strokeWidth={2} />
          {editing ? '편집 완료' : '편집'}
        </button>
      </div>

      {editing ? (
        <CardPicker
          layout={layout}
          capabilities={capabilities}
          onToggle={(id) => applyLayout(toggleCard(layout, id, capabilities))}
          onReset={() => applyLayout(defaultLayout(capabilities))}
        />
      ) : null}

      {/*
        올려 둔 것이 하나도 없을 때. **빈 화면을 그냥 두지 않는다** — 고르는 기능을 만든
        순간 "아무것도 없는 대시보드"가 정상 상태의 하나가 되므로, 거기서 돌아오는 길을
        적어 둔다.
      */}
      {shown.length === 0 && !editing ? (
        <div className="rounded-[10px] border border-dashed border-slate-300 px-4 py-8 text-center">
          <p className="text-[13.5px] text-slate-500">표시할 카드가 없습니다.</p>
          <p className="mt-1 text-[12.5px] text-slate-400">[편집]에서 카드를 추가하세요.</p>
        </div>
      ) : null}

      {/*
        판.

        칸은 셋이고, 좁아지면 둘·하나로 준다. 카드가 차지할 칸 수(`span`)는 사람이 정하지만
        **화면이 좁으면 칸 수도 함께 준다** — 휴대폰에서 세 칸짜리 카드를 그대로 세우면
        격자가 화면 밖으로 넘친다.
      */}
      <div
        ref={gridRef}
        className={`grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-3 ${
          editing ? 'is-editing' : ''
        }`}
      >
        {shown.map((placement) => {
          const card = cardById(placement.id);
          const body = cardBody(placement.id);
          // 지금 보여줄 것이 없는 카드는 그리지 않는다. 편집 중에는 자리를 남겨 둔다 —
          // 내려놓은 것이 아니라 이번에만 비는 것이므로 옮기고 내릴 수 있어야 한다.
          if (!card || (body === null && !editing)) return null;

          return (
            <div
              key={placement.id}
              data-flip={placement.id}
              className={`dash-card ${SPAN_CLASS[placement.span] ?? SPAN_CLASS[1]} ${
                dragId === placement.id ? 'dash-card-lifted' : ''
              } ${placement.height ? 'has-height' : ''}`}
              // 사람이 정한 높이 — 내용이 넘치면 카드 안에서 굴러간다(머리는 남는다).
              style={placement.height ? { height: placement.height } : undefined}
            >
              {body ?? (
                <div className="rounded-[10px] border border-dashed border-slate-300 px-4 py-6 text-center text-[12.5px] text-slate-400">
                  표시할 내용이 없어 편집 중에만 보입니다.
                </div>
              )}
              {/* 겉옷은 카드 **위에** 덮는다 — 그래서 마크업에서도 카드 뒤에 온다. */}
              {editing ? (
                <CardEditMask
                  card={card}
                  span={placement.span}
                  height={placement.height ?? null}
                  resizing={resizeId === placement.id}
                  onGrab={(event) => startCardDrag(placement.id, event)}
                  onResize={(event) => startCardResize(placement.id, event)}
                  onHeight={(height) => {
                    const current = latest.current;
                    const card = current.find((entry) => entry.id === placement.id);
                    if (!card) return;
                    const next = resizeCard(current, placement.id, card.span, height);
                    if (!sameLayout(next, current)) applyLayout(next);
                  }}
                  onMove={(delta) =>
                    moveCard(placement.id, layout.findIndex((entry) => entry.id === placement.id) + delta)
                  }
                  onRemove={() => applyLayout(toggleCard(layout, placement.id, capabilities))}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────── 조각 ───────────────────────────────

export interface SetupStep {
  label: string;
  done: boolean;
  to: string;
  hint: string;
}

/**
 * 시작하기 단계.
 *
 * 순서가 곧 의존 관계다: 화면이 없으면 배정할 곳이 없고, 콘텐츠가 없으면 묶을
 * 것이 없다. 처음 켠 사람이 어디를 눌러도 빈 목록만 보는 상황을 막는 것이 목적이라
 * **완료 판정은 실제 데이터**로 한다 — 눌렀는지가 아니라 실제로 생겼는지를 본다.
 */
export function setupSteps(input: {
  /** 등록된 화면 수(숨긴 것 제외 — 함대 요약의 `total`). */
  displayCount: number;
  /** 그중 편성을 받는 화면 수 — 자체 배정이나 그룹 편성이 있는 것(`total - unassigned`). */
  assignedCount: number;
  assetCount: number;
  playlistCount: number;
  /** 에이전트 화면을 열 수 있는가 — 관리자(공급사)만. 아니면 그 주소는 없다. */
  canInstall?: boolean;
}): SetupStep[] {
  return [
    {
      label: '화면 연결하기',
      done: input.displayCount > 0,
      /*
       * 에이전트 설치는 공급사(관리자)가 정비 모드의 에이전트 화면에서 한다. 사용자에게는 그
       * 화면이 없으니 설치를 말하지 않고 화면 목록으로 보낸다(거기서 기기에 뜬 코드를 넣는다).
       */
      to: input.canInstall === true ? '/agent' : '/displays',
      hint:
        input.canInstall === true
          ? '에이전트를 설치하고 페어링 코드를 입력합니다'
          : '기기에 표시된 페어링 코드를 [화면 추가]에 입력합니다',
    },
    {
      label: '콘텐츠 올리기',
      done: input.assetCount > 0,
      to: '/content',
      hint: '이미지·영상을 올리거나 템플릿을 만듭니다',
    },
    {
      label: '플레이리스트 만들기',
      done: input.playlistCount > 0,
      to: '/playlists',
      hint: '콘텐츠를 재생 순서대로 묶습니다',
    },
    {
      label: '화면에 편성 배정',
      done: input.assignedCount > 0,
      to: '/displays',
      hint: '화면별로 재생할 편성을 지정합니다',
    },
  ];
}

const SETUP_DISMISSED = 'signageone.setup.dismissed';

/**
 * 처음 켰을 때의 안내.
 *
 * 빈 콘솔은 "무엇을 먼저 해야 하는지" 를 알려주지 않는다. 화면·콘텐츠·편성이
 * 서로 물려 있어서, 순서를 모르면 어디를 눌러도 빈 목록만 나온다.
 *
 * **다 끝나면 스스로 사라진다.** 남은 단계가 없는데도 남아 있으면 그때부터는
 * 그 자체가 잡음이다. 중간에 닫을 수도 있게 두되, 그 선택을 기억한다.
 */
function SetupChecklist({ steps }: { steps: SetupStep[] }): ReactNode {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(SETUP_DISMISSED) === '1';
    } catch {
      return false;
    }
  });

  const remaining = steps.filter((step) => !step.done);
  if (dismissed || remaining.length === 0) return null;

  return (
    <section className="rounded-[10px] border border-th-6 bg-th-2/50 px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[13.5px] font-semibold text-slate-800">시작하기</h2>
          <p className="mt-0.5 text-[12.5px] text-slate-600">
            {steps.length - remaining.length} / {steps.length} 단계 완료
          </p>
        </div>
        <button
          type="button"
          aria-label="시작하기 안내 닫기"
          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-white/60 hover:text-slate-600"
          onClick={() => {
            setDismissed(true);
            try {
              window.localStorage.setItem(SETUP_DISMISSED, '1');
            } catch {
              // 저장이 막혀도 이번 세션 동안은 닫힌다.
            }
          }}
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      <ol className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {steps.map((step, index) => (
          <li key={step.to}>
            <Link
              to={step.to}
              className={`flex h-full items-start gap-2.5 rounded-lg border bg-white px-3 py-2.5 transition-colors ${
                step.done ? 'border-slate-200 opacity-60' : 'border-slate-200 hover:border-th-4'
              }`}
            >
              <span
                className={`mt-0.5 flex h-4.5 w-[18px] shrink-0 items-center justify-center rounded-full text-[11.5px] font-semibold ${
                  step.done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                }`}
              >
                {step.done ? <Check className="h-3 w-3" strokeWidth={3} /> : index + 1}
              </span>
              <span className="min-w-0">
                <span
                  className={`block text-[12.5px] font-medium ${
                    step.done ? 'text-slate-500 line-through' : 'text-slate-800'
                  }`}
                >
                  {step.label}
                </span>
                <span className="mt-0.5 block text-[11.5px] leading-snug text-slate-400">{step.hint}</span>
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * 이 브라우저에 남겨 둔 **내** 선택. 저장이 막혀 있으면(사생활 보호 창 등) 기본값으로 연다.
 *
 * 계정을 가리지 않던 옛 사본은 여기서 지운다 — 공용 PC 라면 남의 판이다.
 */
function readStoredCards(key: string): string | null {
  try {
    window.localStorage.removeItem(LEGACY_DASHBOARD_CARDS_KEY);
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * 판을 이 브라우저에도 남긴다 — 다음에 열 때 **첫 그림**을 서버 응답 없이 그리기 위해서다.
 *
 * 진짜 저장소는 계정(서버)이다. 저장이 막혀 있어도(사생활 보호 창 등) 잃는 것은 첫 그림의
 * 매끄러움뿐이다.
 */
function rememberCards(key: string, layout: readonly CardPlacement[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(layout));
  } catch {
    // 이 브라우저가 기억하지 못할 뿐, 계정에는 남는다.
  }
}

/**
 * 칸 수 → 격자 클래스.
 *
 * 문자열을 만들어 붙이지 않고 표로 둔다 — Tailwind 는 소스에 **그대로 적힌** 클래스만
 * 만들어 낸다. `xl:col-span-${n}` 처럼 이어 붙이면 개발 중에는 되다가 빌드에서 사라진다.
 *
 * 좁은 화면에서는 칸 수도 함께 준다. 두 칸짜리 격자에서 `span 3` 을 그대로 쓰면 브라우저가
 * 없는 세 번째 칸을 만들어 내어 판이 화면 밖으로 넘친다.
 */
const SPAN_CLASS: Record<number, string> = {
  1: 'md:col-span-1 xl:col-span-1',
  2: 'md:col-span-2 xl:col-span-2',
  3: 'md:col-span-2 xl:col-span-3',
};

/** 모서리를 세로로 이만큼(px) 넘게 끌어야 높이를 바꾼다 — 폭만 바꾸려던 손의 흔들림으로 높이가 고정되지 않게. */
const HEIGHT_DRAG_FROM = 12;
/** 키보드(Alt + ↑/↓)로 높이를 바꾸는 한 걸음(px). */
const HEIGHT_KEY_STEP = 40;

/**
 * 편집 중인 카드에 씌우는 겉옷 — 잡는 자리, 크기 모서리, 내리기.
 *
 * **카드 위에 손잡이 줄을 얹지 않는다.** 예전에는 카드마다 머리 줄이 하나씩 붙어, 편집을
 * 켜는 순간 판이 위아래로 늘어나며 카드들이 제자리를 잃었다 — 자리를 고치려고 켠 모드가
 * 자리를 흔들어 놓는 셈이었다. 손잡이는 카드 **안쪽**에 겹쳐 둔다.
 *
 * 겉옷은 카드를 덮는다. 편집 중에 카드 속 링크를 눌러 다른 화면으로 떠나 버리는 일을 막고,
 * 카드 어디를 잡아도 끌 수 있게 한다 — 잡을 자리를 따로 찾지 않아도 된다.
 */
function CardEditMask({
  card,
  span,
  height,
  resizing,
  onGrab,
  onResize,
  onHeight,
  onMove,
  onRemove,
}: {
  card: DashboardCard;
  span: number;
  /** 사람이 정한 높이(px). `null` 이면 내용 높이. */
  height: number | null;
  /** 지금 이 카드의 모서리를 끄는 중인가. 그동안은 칸 수 표시를 남긴다. */
  resizing: boolean;
  onGrab: (event: ReactPointerEvent<HTMLElement>) => void;
  onResize: (event: ReactPointerEvent<HTMLElement>) => void;
  /** 높이를 정한다(px) — `null` 이면 내용 높이로. */
  onHeight: (height: number | null) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}): ReactNode {
  return (
    <div
      className="dash-mask"
      onPointerDown={onGrab}
      // 길게 누르면 뜨는 메뉴가 손가락 끌기를 가로막는다.
      onContextMenu={(event) => event.preventDefault()}
    >
      {/*
        이름표가 곧 잡는 자리다. 키보드만 쓰는 사람에게 끌기는 없는 기능이라, 여기에
        초점을 두고 Alt + ←/→ 로 옮긴다.
      */}
      <button
        type="button"
        className="dash-grip"
        onKeyDown={(event) => {
          if (!event.altKey) return;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            onMove(event.key === 'ArrowLeft' ? -1 : 1);
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            // 높이 — 정한 적이 없으면 지금 보이는 높이에서 시작한다.
            event.preventDefault();
            const shown =
              height ?? event.currentTarget.closest<HTMLElement>('[data-flip]')?.getBoundingClientRect().height ?? 0;
            onHeight(shown + (event.key === 'ArrowUp' ? -HEIGHT_KEY_STEP : HEIGHT_KEY_STEP));
          }
        }}
        title="끌어서 이동 (Alt + ← →, 높이 Alt + ↑ ↓)"
        aria-label={`${card.title} 자리 옮기기`}
      >
        <span aria-hidden="true">⠿</span>
        <span className="truncate">{card.title}</span>
      </button>

      <button
        type="button"
        className="dash-drop"
        data-no-drag
        title="카드 제거"
        aria-label={`${card.title} 제거`}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={onRemove}
      >
        <X className="h-3.5 w-3.5" strokeWidth={2.2} />
      </button>

      {/*
        크기 모서리 — 오른쪽 아래. 모니터링의 화면 카드와 **같은 모양·같은 커서**를 쓴다.
        콘솔 안에서 "여기를 끌면 커진다" 가 화면마다 다르게 생겼으면 매번 다시 배워야 한다.
      */}
      <span className={`dash-size ${resizing ? 'is-active' : ''}`} aria-hidden="true">
        {span}/{GRID_COLUMNS} · {height ? `높이 ${height}` : '높이 자동'}
      </span>
      {/* 정한 높이를 거두고 내용 높이로 — 정했을 때만. */}
      {height ? (
        <button
          type="button"
          className="dash-height-auto"
          data-no-drag
          title="내용 높이로 되돌립니다"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => onHeight(null)}
        >
          높이 자동
        </button>
      ) : null}
      <span
        className={`wall-tile-resize ${resizing ? 'is-active' : ''}`}
        aria-hidden="true"
        title="끌어서 폭·높이 조절"
        onPointerDown={onResize}
      />
    </div>
  );
}

/**
 * 올릴 카드 고르기.
 *
 * **고르는 자리가 곧 카드 설명서다.** 이름만 늘어놓으면 "함대 요약"과 "화면 현황"이
 * 무엇이 다른지 눌러 보기 전에는 알 수 없다. 한 줄 설명을 붙여, 고르는 순간에 무엇이
 * 올라오는지 알게 한다.
 */
function CardPicker({
  layout,
  capabilities,
  onToggle,
  onReset,
}: {
  layout: readonly CardPlacement[];
  capabilities: readonly string[];
  onToggle: (id: string) => void;
  onReset: () => void;
}): ReactNode {
  const cards = availableCards(capabilities);

  return (
    <section className="rounded-[10px] border border-th-6 bg-th-2/50 px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[13.5px] font-semibold text-slate-800">
            카드 선택
            <HelpTip
              text="카드를 끌어 순서를 바꾸고, 오른쪽 아래 모서리를 끌어 폭과 높이를 조절합니다([높이 자동] 으로 내용 높이로). 배치는 내 계정에만 저장됩니다."
              label="카드 선택 도움말"
            />
          </h2>
        </div>
        <button type="button" className="btn btn-tiny shrink-0" onClick={onReset}>
          기본값으로
        </button>
      </div>

      <ul className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => {
          const picked = layout.some((placement) => placement.id === card.id);
          return (
            <li key={card.id}>
              <label
                className={`flex h-full cursor-pointer items-start gap-2.5 rounded-lg border bg-white px-3 py-2.5 transition-colors ${
                  picked ? 'border-th-4' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 shrink-0"
                  checked={picked}
                  onChange={() => onToggle(card.id)}
                />
                <span className="min-w-0">
                  <span className="block text-[12.5px] font-medium text-slate-800">{card.title}</span>
                  <span className="mt-0.5 block text-[11.5px] leading-snug text-slate-400">{card.hint}</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

interface PlayToday {
  today: { plays: number; seconds: number };
  /** 어제 **같은 시각까지**. 하루 전체가 아니다. */
  yesterday: { plays: number; seconds: number };
  /** 최근 `PROOF_STALL_MS` 동안 들어온 재생 기록 수. 0 이면 수집이 끊겼을 수 있다. */
  recentPlays: number;
}

/**
 * 오늘의 재생 실적 — 세 번 묻는다.
 *
 * 리포트 화면은 30일 정산용이라 "오늘 잘 나가고 있나" 에 답하지 않는다. 여기서는 오늘
 * 지금까지, 어제 같은 시각까지, 그리고 **최근 15분**을 묻는다. 셋째 질의가 알람 4번
 * ("온라인인데 증빙 0건")의 눈이다.
 *
 * `(tenant, played_at)` 인덱스를 그대로 타는 범위 질의라 셋을 한꺼번에 물어도 가볍다.
 */
async function loadPlayToday(client: SignageOneClient): Promise<PlayToday> {
  const tz = browserTimeZone();
  const now = new Date();
  const nowIso = now.toISOString();
  const dayAgo = new Date(now.getTime() - 86_400_000);

  const [today, yesterday, recent] = await Promise.all([
    client.proofOfPlay({ from: dayStartIso(localDate(now)), to: nowIso, group_by: 'day', tz }),
    client.proofOfPlay({
      from: dayStartIso(localDate(dayAgo)),
      to: dayAgo.toISOString(),
      group_by: 'day',
      tz,
    }),
    client.proofOfPlay({
      from: new Date(now.getTime() - PROOF_STALL_MS).toISOString(),
      to: nowIso,
      group_by: 'day',
      tz,
    }),
  ]);

  return { today: today.totals, yesterday: yesterday.totals, recentPlays: recent.totals.plays };
}

/**
 * 오늘의 재생 실적.
 *
 * **숫자보다 경보가 먼저다.** 이 카드를 대시보드에 올리는 이유의 절반은 "증빙이 끊긴
 * 것을 정산 날에 알게 되는 일"을 막는 것이다. 끊겼으면 숫자 위에 그렇게 적는다.
 */
function PlayTodayCard({
  data,
  error,
  loading,
  playing,
}: {
  data: PlayToday | null;
  error: string | null;
  loading: boolean;
  /** 지금 증빙을 남기고 있어야 하는 화면 수. 함대 요약을 아직 못 받았으면 null. */
  playing: number | null;
}): ReactNode {
  const delta = data ? playDelta(data.today.plays, data.yesterday.plays) : null;
  const stalled = data !== null && playing !== null && proofStalled(data.recentPlays, playing);

  return (
    <Card title="오늘 재생 실적" action={{ label: '리포트', to: '/reports' }}>
      {error ? (
        <p className="p-6 text-[13.5px] text-slate-400">재생 실적을 불러오지 못했습니다: {error}</p>
      ) : !data ? (
        <p className="p-6 text-[13.5px] text-slate-400">{loading ? '불러오는 중…' : '기록이 없습니다.'}</p>
      ) : (
        <>
          {stalled ? (
            <div
              className="flex items-start gap-2 border-b border-red-100 bg-red-50 px-4 py-3 text-[12.5px] text-red-700"
              role="status"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
              <span>
                <strong className="font-semibold">
                  {Math.round(PROOF_STALL_MS / 60_000)}분째 재생 기록이 없습니다.
                </strong>{' '}
                재생 중이어야 할 화면 {playing}대의 재생 상태와 증빙 전송을 확인하세요. 기록되지 않은 시간은
                정산에서 빠집니다.
              </span>
            </div>
          ) : null}

          <div className="grid gap-4 p-4 sm:grid-cols-3">
            <div>
              <p className="text-[11.5px] font-medium text-slate-500">
                오늘 재생
                <HelpTip text="오늘 0시부터 지금까지의 재생 횟수입니다. 어제 같은 시각까지의 횟수와 비교합니다." label="오늘 재생 도움말" />
              </p>
              <p className="mt-1.5 flex items-baseline gap-1.5">
                <span className="font-mono text-[26px] font-semibold leading-none tracking-tight text-slate-900">
                  {data.today.plays.toLocaleString('ko-KR')}
                </span>
                <span className="font-mono text-[13.5px] text-slate-400">회</span>
              </p>
              {delta ? (
                <p
                  className={`mt-1.5 text-[11.5px] ${
                    delta.tone === 'warn'
                      ? 'text-amber-600'
                      : delta.tone === 'ok'
                        ? 'text-emerald-600'
                        : 'text-slate-400'
                  }`}
                >
                  {delta.text}
                </p>
              ) : null}
            </div>

            <div>
              <p className="text-[11.5px] font-medium text-slate-500">오늘 노출 시간</p>
              <p className="mt-1.5 font-mono text-[20px] font-semibold leading-none tracking-tight text-slate-900">
                {formatSeconds(data.today.seconds)}
              </p>
              <p className="mt-1.5 text-[11.5px] text-slate-400">
                어제 같은 시각 {formatSeconds(data.yesterday.seconds)}
              </p>
            </div>

            <div>
              <p className="text-[11.5px] font-medium text-slate-500">
                최근 {Math.round(PROOF_STALL_MS / 60_000)}분
                <HelpTip
                  text="최근 들어온 재생 기록 수입니다. 재생 대상 화면(온라인 중 정비 모드·미배정 제외)이 있는데 0건이면 경보를 표시합니다."
                  label="최근 재생 기록 도움말"
                />
              </p>
              <p className="mt-1.5 font-mono text-[20px] font-semibold leading-none tracking-tight text-slate-900">
                {data.recentPlays.toLocaleString('ko-KR')}
              </p>
              {/* 숫자만으로는 0 이 정상인지 아닌지 알 수 없다 — 그 판단의 근거를 함께 적는다. */}
              <p className="mt-1.5 text-[11.5px] text-slate-400">
                {playing === null
                  ? '건'
                  : playing > 0
                    ? `건 · 재생 대상 화면 ${playing}대`
                    : '건 · 재생 대상 화면 없음'}
              </p>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * 요약 숫자 하나.
 *
 * 아이콘 타일을 뺐다. 색 있는 사각형 네 개가 숫자 네 개와 나란히 서면 눈이
 * 어디를 봐야 할지 정하지 못한다. 카드 한 장에 **주인공은 숫자 하나**다.
 */
function StatCard({
  to,
  label,
  value,
  suffix,
  foot,
  footTone,
}: {
  to: string;
  label: string;
  value: string;
  suffix?: string;
  foot: string;
  footTone: 'ok' | 'warn' | 'danger' | 'muted';
}): ReactNode {
  const footColor = {
    ok: 'text-emerald-600',
    warn: 'text-amber-600',
    danger: 'text-red-600',
    muted: 'text-slate-400',
  }[footTone];

  return (
    <Link
      to={to}
      className="rounded-[10px] border border-slate-200 bg-white px-4 py-3.5 transition-colors hover:border-slate-300"
    >
      <p className="text-[11.5px] font-medium text-slate-500">{label}</p>
      <p className="mt-1.5 flex items-baseline gap-1.5">
        <span className="font-mono text-[26px] font-semibold leading-none tracking-tight text-slate-900">
          {value}
        </span>
        {suffix ? <span className="font-mono text-[13.5px] text-slate-400">{suffix}</span> : null}
      </p>
      <p className={`mt-1.5 text-[11.5px] ${footColor}`}>{foot}</p>
    </Link>
  );
}

/**
 * 구역 하나.
 *
 * 제목 앞의 아이콘 타일을 뺐다. 구역마다 색 타일이 붙으면 그것들끼리 리듬을
 * 만들어, 정작 안에 있는 데이터보다 껍데기가 먼저 읽힌다.
 */
function Card({
  title,
  badge,
  action,
  children,
}: {
  title: string;
  badge?: string;
  action?: { label: string; to: string };
  children: ReactNode;
}): ReactNode {
  return (
    <section className="overflow-hidden rounded-[10px] border border-slate-200 bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5">
        <div className="flex items-baseline gap-2">
          <h2 className="text-[13.5px] font-semibold text-slate-800">{title}</h2>
          {badge ? <span className="text-[11.5px] text-slate-400">{badge}</span> : null}
        </div>
        {action ? (
          <Link
            to={action.to}
            className="flex shrink-0 items-center gap-0.5 text-[12.5px] text-slate-500 transition-colors hover:text-slate-800"
          >
            {action.label}
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        ) : null}
      </header>
      {children}
    </section>
  );
}

/**
 * 유형 분포 도넛.
 *
 * 차트 라이브러리를 넣지 않았다 — 조각 네 개짜리 도넛 하나에 수십 kB 를 쓸 이유가
 * 없고, `stroke-dasharray` 로 정확히 같은 그림이 나온다.
 */
function TypeDonut({
  counts,
  total,
}: {
  counts: Partial<Record<Asset['type'], number>>;
  total: number;
}): ReactNode {
  const entries = (Object.keys(TYPE_LABEL) as Asset['type'][])
    .map((type) => ({ type, count: counts[type] ?? 0 }))
    .filter((entry) => entry.count > 0);

  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex flex-col items-center gap-3 p-4">
      {total === 0 ? (
        <p className="py-8 text-[13.5px] text-slate-400">콘텐츠가 없습니다.</p>
      ) : (
        <>
          <div className="relative">
            <svg width="124" height="124" viewBox="0 0 124 124" role="img" aria-label={`전체 ${total}개`}>
              <circle cx="62" cy="62" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="16" />
              {entries.map((entry) => {
                const length = (entry.count / total) * circumference;
                const dash = `${length} ${circumference - length}`;
                const rotation = (offset / circumference) * 360 - 90;
                offset += length;
                return (
                  <circle
                    key={entry.type}
                    cx="62"
                    cy="62"
                    r={radius}
                    fill="none"
                    stroke={TYPE_COLOR[entry.type]}
                    strokeWidth="16"
                    strokeDasharray={dash}
                    transform={`rotate(${rotation} 62 62)`}
                  />
                );
              })}
            </svg>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono text-[20px] font-semibold leading-none text-slate-900">{total}</span>
              <span className="mt-1 text-[11.5px] text-slate-400">전체 콘텐츠</span>
            </div>
          </div>

          <ul className="grid w-full grid-cols-2 gap-x-4 gap-y-1.5">
            {entries.map((entry) => (
              <li key={entry.type} className="flex items-center gap-2 text-[12.5px]">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: TYPE_COLOR[entry.type] }}
                />
                <span className="flex-1 truncate text-slate-600">{TYPE_LABEL[entry.type]}</span>
                <span className="font-mono text-slate-500">{entry.count}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── 오늘의 재생 실적 ───────────────────────────

/**
 * 증빙이 끊겼다고 볼 침묵의 길이.
 *
 * 운영 문서의 알람 4번과 같은 15분이다(`docs/OPERATIONS.md`). 화면은 폴링 주기(기본
 * 30초)마다 증빙을 밀어 올리므로, 15분은 회선이 잠깐 끊긴 것과 수집이 죽은 것을 가르기에
 * 넉넉하다 — 짧게 잡으면 잠깐의 재시도마다 경보가 울리고, 그런 경보는 곧 무시된다.
 */
export const PROOF_STALL_MS = 15 * 60_000;

/**
 * 지금 증빙을 남기고 있어야 하는 화면 수.
 *
 * **모자라게 센다.** 온라인이라고 다 증빙을 남기는 것이 아니다 — 정비 모드는 재생 자체가
 * 멈춰 있고, 아무 편성도 받지 못한 화면은 틀 것이 없고, 운영 시간 밖의 화면은 쉬고 있다. 둘을 빼지 않으면 "정상인데
 * 조용한" 상태를 장애라고 부르게 되고, 그런 경보는 한 번 겪고 나면 아무도 보지 않는다.
 *
 * 미배정 화면이 꺼져 있으면 두 번 빼는 셈이라 실제보다 적게 나오지만, 그 방향의 실수는
 * "경보를 늦게 울리는 것"이다 — 반대 방향(헛경보)보다 낫다.
 */
export function playingDisplays(
  summary: Pick<FleetSummary, 'online' | 'maintenance' | 'unassigned' | 'closed'>,
): number {
  // 운영 시간 밖이라 쉬는 화면도 뺀다 — 세면 밤마다 "증빙 끊김" 이 빨갛게 뜬다.
  return Math.max(0, summary.online - summary.maintenance - summary.unassigned - (summary.closed ?? 0));
}

/**
 * 증빙 수집이 끊겼는가 — 틀고 있어야 할 화면이 있는데 최근 창에 한 건도 없다.
 *
 * 화면이 다 꺼져 있거나 아무것도 편성되지 않았으면 조용한 것이 정상이다. 그때 경보를
 * 울리면 "설정하기 전"과 "고장"이 같은 얼굴이 된다.
 */
export function proofStalled(recentPlays: number, playing: number): boolean {
  return playing > 0 && recentPlays === 0;
}

/**
 * 어제 같은 시각과 견준 한 줄.
 *
 * **어제 하루 전체와 견주지 않는다.** 아침 아홉 시의 오늘은 어제 하루를 이길 수 없어,
 * 매일 아침 "어제보다 80% 감소" 를 보게 된다 — 늘 빨간 숫자는 아무것도 말하지 않는다.
 */
export function playDelta(today: number, yesterday: number): { text: string; tone: 'ok' | 'warn' | 'muted' } {
  if (yesterday === 0) {
    return today > 0
      ? { text: '어제 같은 시각 기록 없음', tone: 'muted' }
      : { text: '어제 같은 시각에도 기록 없음', tone: 'muted' };
  }
  const ratio = Math.round(((today - yesterday) / yesterday) * 100);
  if (ratio === 0) return { text: '어제 같은 시각과 동일', tone: 'muted' };
  const sign = ratio > 0 ? '+' : '';
  return {
    text: `어제 같은 시각 대비 ${sign}${ratio}% (${yesterday.toLocaleString('ko-KR')}회)`,
    // 재생이 줄어든 것은 늘 확인할 거리다 — 늘었으면 그냥 사실이다.
    tone: ratio < 0 ? 'warn' : 'ok',
  };
}

// ─────────────────────────────── 집계 ───────────────────────────────

/**
 * "주의 필요" 카드 아래 한 줄.
 *
 * **0 인 항목은 적지 않는다** — "미배정 0" 은 정보가 아니라 잡음이다.
 *
 * 정비 모드는 위의 숫자(오프라인 + 오류)에는 들어가지 않지만 여기서는 말한다.
 * 온라인이면서 **재생과 증빙이 멈춘** 상태이고 콘솔에서는 풀 수 없어 현장에 연락해야
 * 하는데, 예전에는 화면을 한 대씩 열어야만 알 수 있었다 — 서버는 처음부터 세어
 * 보내고 있었다.
 */
export function attentionFoot(
  summary: Pick<FleetSummary, 'offline' | 'erroring' | 'unassigned' | 'maintenance'>,
): string {
  const parts = [
    summary.offline > 0 ? `오프라인 ${summary.offline}` : null,
    summary.erroring > 0 ? `오류 ${summary.erroring}` : null,
    summary.maintenance > 0 ? `정비 모드 ${summary.maintenance}` : null,
    summary.unassigned > 0 ? `미배정 ${summary.unassigned}` : null,
  ].filter(Boolean);

  return parts.join(' · ') || '모두 정상';
}

export interface LocationRow {
  name: string;
  total: number;
  online: number;
  offline: number;
}

/**
 * 위치별 집계를 그릴 줄로. 위치가 비어 있는 화면도 버리지 않는다 — 그런 화면이야말로 눈에 띄어야 한다.
 *
 * 세는 것은 서버다(`/displays/summary?locations=1`, 숨긴 화면 제외 — 위의 숫자들과 같은 집합). 서버는
 * 위치를 다듬어(앞뒤 공백) 묶으므로, 빈 이름이 곧 미지정이다. 같은 이름이 두 번 오면(옛 서버) 합친다.
 *
 * **묶는 값은 `location` 이다.** 예전에는 `region` 으로 묶었는데, 그 값은 등록할 때 API 로
 * 한 번 들어오고 콘솔에는 고치는 칸조차 없었다 — 운영자가 화면 목록에서 거르고, 일괄로
 * 옮기고, 모니터링 벽이 구획으로 쓰는 것은 `location` 이다.
 */
export function locationRows(rows: readonly LocationRow[]): LocationRow[] {
  const map = new Map<string, LocationRow>();
  for (const row of rows) {
    /* 목록의 "전체 위치" 거르개와 같은 규칙 — 빈 값과 공백만 있는 값은 미지정이다. */
    const name = row.name.trim() || '위치 미지정';
    const merged = map.get(name) ?? { name, total: 0, online: 0, offline: 0 };
    merged.total += row.total;
    merged.online += row.online;
    merged.offline += row.offline;
    map.set(name, merged);
  }
  // 문제가 많은 위치가 위로 온다.
  return [...map.values()].sort((a, b) => b.offline - a.offline || b.total - a.total);
}

/**
 * 화면 현황 카드에 올릴 화면 — 손볼 화면(`status=attention`)이 먼저, 모자라면 첫 쪽의 화면으로 채운다.
 *
 * 문제 있는 것부터 보인다 — 정상인 화면은 볼 이유가 없다. 손볼 화면이 여덟 대를 넘으면 그 여덟 대만,
 * 하나도 없으면 첫 쪽의 정상 화면들이 선다(빈 카드보다 "다 괜찮다" 가 보이는 편이 낫다).
 */
export function displayCardRows(
  attention: readonly Display[],
  first: readonly Display[],
  plannedGroups: ReadonlySet<string>,
  rows = DISPLAY_CARD_ROWS,
): Display[] {
  const seen = new Set<string>();
  const merged: Display[] = [];
  for (const display of [...attention, ...first]) {
    if (seen.has(display.id)) continue;
    seen.add(display.id);
    merged.push(display);
  }
  return merged.sort((a, b) => rank(a, plannedGroups) - rank(b, plannedGroups)).slice(0, rows);
}

export function countTypes(assets: readonly Asset[]): Partial<Record<Asset['type'], number>> {
  const counts: Partial<Record<Asset['type'], number>> = {};
  for (const asset of assets) counts[asset.type] = (counts[asset.type] ?? 0) + 1;
  return counts;
}

/** 그룹 편성을 아직 모를 때 — 그룹 화면도 미배정으로 본다(받기 전 잠깐이다). */
const NO_GROUPS: ReadonlySet<string> = new Set();

/**
 * 아무 편성도 받지 못하는 화면인가.
 *
 * **그룹에 속하면 그룹의 편성을 물려받는다 — 그룹에 편성이 있을 때만.** 화면 자체 배정만 보면 그룹으로 편성받는
 * 화면이 전부 "미배정" 으로 찍혔고, 거꾸로 그룹에 들기만 하면 배정된 것으로 보면 편성 없는 그룹의 화면이 대기
 * 화면만 내보내면서도 빠졌다(2026-10-19 G5). 서버 `/displays/summary` 의 `unassigned` 와 같은 기준
 * (`services/assigned.ts`) — `plannedGroups` 는 그룹 편성이 있는 그룹의 id(`plannedGroupIds`).
 */
export function isUnassigned(display: Pick<Display, 'assignment' | 'group_id'>, plannedGroups: ReadonlySet<string>): boolean {
  return display.assignment.kind === 'none' && !(display.group_id !== null && plannedGroups.has(display.group_id));
}

/** 목록 정렬 우선순위 — 오류 → 오프라인 → 미배정 → 정상. 끊긴 화면의 마지막 오류는 오류로 치지 않는다(`displayLive`). */
export function rank(display: Display, plannedGroups: ReadonlySet<string>): number {
  if (displayLive(display).error) return 0;
  if (!display.online) return 1;
  if (isUnassigned(display, plannedGroups)) return 2;
  return 3;
}
